// ./src/components/features/Rentals/RentalsList/RentalsList.tsx
//
// Event rentals list — table on desktop (>= md), cards on tablet/mobile. Tap-safe
// (explicit actions only), rights-gated per row, selection-ready. Each row shows the
// vendor, the pickup → drop-off (place + local datetime), the derived total cost, a
// lifecycle-status badge and a condition-media count. The inline "Mark out" /
// "Mark returned" actions advance the status with a PER-BUTTON spinner. Mirrors
// BudgetItemsList (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
  Badge,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import {
  EventRental,
  RENTAL_STATUS,
  RENTAL_STATUS_LABELS,
  venueRefName,
} from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { AppIcon, EmptyState, SwipeableCard, SwipeAction, useSwipeableList, LoadMoreButton, CardSkeletonActions, CardSkeletonCheckbox, CardSkeletonLines, countCardActions, type CardSkeletonShape } from "@/components/generic";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG, formatCurrency } from "@/utils";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface RentalsListProps {
  data?: EventRental[];
  loading?: boolean;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; a
   * refetch keeps the rows OR the empty state on screen and lets ContentLoadingOverlay say it is
   * reloading. `loading && items.length === 0` re-showed it on every refetch of an EMPTY list, so
   * an empty page read skeleton → "none" → skeleton → "none". Required so a caller cannot drop it.
   */
  isLoaded: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditRental?: (rental: EventRental) => void;
  onDeleteRental?: (rental: EventRental) => void;
  onManageMedia?: (rental: EventRental) => void;
  /** Advance a rental's lifecycle status inline (per-button spinner). */
  onSetStatus?: (rental: EventRental, next: string) => void;
  /** Per-button in-flight test: isUpdating(id, `status:<value>`). */
  isUpdating?: (id?: string, actionKey?: string) => boolean;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddRental?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  hasQueryNarrowing?: boolean;
}

const STATUS_COLOR: Record<string, string> = {
  [RENTAL_STATUS.RESERVED]: "blue",
  [RENTAL_STATUS.OUT]: "orange",
  [RENTAL_STATUS.RETURNED]: "teal",
  [RENTAL_STATUS.CANCELED]: "gray",
};

const rentalCurrency = (rental: EventRental): string => rental.currencyCode || "USD";

const formatInZone = (iso: string | null | undefined, tz: string | null | undefined): string | null => {
  if (!iso) {
    return null;
  }

  const d = tz ? dayjs.utc(iso).tz(tz) : dayjs(iso);

  if (!d.isValid()) {
    return null;
  }

  return d.format("MMM D, h:mm A");
};

interface EndpointInfo {
  place: string | null;
  when: string | null;
}

const pickupInfo = (r: EventRental): EndpointInfo => ({
  place: venueRefName(r.pickupVenue, r.pickupLocationName) || r.pickupAddress || null,
  when: formatInZone(r.pickupAt, r.pickupTimezone),
});

const dropoffInfo = (r: EventRental): EndpointInfo => ({
  place: venueRefName(r.dropoffVenue, r.dropoffLocationName) || r.dropoffAddress || null,
  when: formatInZone(r.dropoffAt, r.dropoffTimezone),
});

const StatusBadge: React.FC<{ rental: EventRental }> = ({ rental }) => {
  const status = rental.rentalStatus || RENTAL_STATUS.RESERVED;

  return (
    <Badge size="sm" variant="light" color={STATUS_COLOR[status] || "gray"}>
      {RENTAL_STATUS_LABELS[status] || status}
    </Badge>
  );
};

const EndpointCell: React.FC<{ info: EndpointInfo; iconName: string; emptyLabel: string }> = ({ info, iconName, emptyLabel }) => {
  if (!info.place && !info.when) {
    return (
      <Text size="sm" c="dimmed">
        {emptyLabel}
      </Text>
    );
  }

  return (
    <Box style={{ minWidth: 0 }}>
      <Group gap={6} wrap="nowrap">
        <AppIcon iconName={iconName} size={14} />
        <Text size="md" lineClamp={1}>
          {info.place || "—"}
        </Text>
      </Group>
      {info.when ? (
        <Text size="xs" c="dimmed">
          {info.when}
        </Text>
      ) : null}
    </Box>
  );
};

const RouteCell: React.FC<{ rental: EventRental }> = ({ rental }) => {
  const { t } = useTranslation(["rentals"]);

  return (
    <Stack gap={4}>
      <EndpointCell
        info={pickupInfo(rental)}
        iconName="ArrowUpRight"
        emptyLabel={t("rentals:list.noPickup", { defaultValue: "No pickup set" })}
      />
      <EndpointCell
        info={dropoffInfo(rental)}
        iconName="ArrowDownRight"
        emptyLabel={t("rentals:list.noDropoff", { defaultValue: "No drop-off set" })}
      />
    </Stack>
  );
};

const VendorCell: React.FC<{ rental: EventRental }> = ({ rental }) => {
  const name = venueRefName(rental.vendor, rental.vendorLabel);

  if (!name) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      <AppIcon iconName="Briefcase" size={15} />
      <Text size="md" lineClamp={1}>
        {name}
      </Text>
    </Group>
  );
};

const CostCell: React.FC<{ rental: EventRental }> = ({ rental }) => (
  <Text size="md" fw={700}>
    {formatCurrency(rental.totalAmount ?? 0, rentalCurrency(rental))}
  </Text>
);

const MediaBadge: React.FC<{ rental: EventRental }> = ({ rental }) => {
  const count = rental.media?.length ?? 0;

  if (count === 0) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="grape" leftSection={<AppIcon iconName="Photo" size={11} />}>
      {count}
    </Badge>
  );
};

const RentalIcon: React.FC<{ size?: number }> = ({ size = 34 }) => (
  <ThemeIcon variant="light" color="grape" radius="xl" size={size}>
    <AppIcon iconName="Package" size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

interface StatusActionMeta {
  next: string;
  label: string;
  icon: string;
  color: string;
}

const nextStatusAction = (rental: EventRental, t: any): StatusActionMeta | null => {
  const status = rental.rentalStatus || RENTAL_STATUS.RESERVED;

  if (status === RENTAL_STATUS.RESERVED) {
    return { next: RENTAL_STATUS.OUT, label: t("rentals:list.markOut", { defaultValue: "Mark as received" }), icon: "Key", color: "orange" };
  }

  if (status === RENTAL_STATUS.OUT) {
    return { next: RENTAL_STATUS.RETURNED, label: t("rentals:list.markReturned", { defaultValue: "Mark as returned" }), icon: "PackageImport", color: "teal" };
  }

  return null;
};

const RowActions: React.FC<{
  rental: EventRental;
  onEdit?: (r: EventRental) => void;
  onDelete?: (r: EventRental) => void;
  onMedia?: (r: EventRental) => void;
  onSetStatus?: (r: EventRental, next: string) => void;
  isUpdating: (id?: string, actionKey?: string) => boolean;
  isDeleting: boolean;
}> = ({ rental, onEdit, onDelete, onMedia, onSetStatus, isUpdating, isDeleting }) => {
  const { t } = useTranslation(["rentals", "common"]);

  const canEdit = recordHasRight(rental, "edit");
  const showEdit = !!onEdit && canEdit;
  const showDelete = !!onDelete && recordHasRight(rental, "remove");
  const showMedia = !!onMedia;
  const statusAction = onSetStatus && canEdit ? nextStatusAction(rental, t) : null;

  const rowBusy = isUpdating(rental.id);

  if (!showEdit && !showDelete && !showMedia && !statusAction) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {statusAction && onSetStatus && (
        <Tooltip label={statusAction.label} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color={statusAction.color}
            size="md"
            onClick={() => onSetStatus(rental, statusAction.next)}
            loading={isUpdating(rental.id, `status:${statusAction.next}`)}
            disabled={(rowBusy && !isUpdating(rental.id, `status:${statusAction.next}`)) || isDeleting}
            aria-label={statusAction.label}
          >
            <AppIcon iconName={statusAction.icon} size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showMedia && onMedia && (
        <Tooltip label={t("rentals:list.media", { defaultValue: "Condition media" })} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="grape"
            size="md"
            onClick={() => onMedia(rental)}
            disabled={isDeleting}
            aria-label={t("rentals:list.media", { defaultValue: "Condition media" })}
          >
            <AppIcon iconName="Photo" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(rental)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(rental)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

interface RowProps {
  rental: EventRental;
  name: string;
  onEdit?: (r: EventRental) => void;
  onDelete?: (r: EventRental) => void;
  onMedia?: (r: EventRental) => void;
  onSetStatus?: (r: EventRental, next: string) => void;
  isUpdating: (id?: string, actionKey?: string) => boolean;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

const RentalTableRow: React.FC<RowProps> = ({
  rental,
  name,
  onEdit,
  onDelete,
  onMedia,
  onSetStatus,
  isUpdating,
  highlighted,
  isDeleting,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => (
  <Table.Tr
    style={{
      verticalAlign: "top",
      opacity: isDeleting ? 0.5 : 1,
      backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : selected ? "var(--mantine-color-blue-light)" : undefined,
      transition: "background-color 150ms ease, opacity 150ms ease",
    }}
  >
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        {selectable && (
          <Checkbox
            mt={7}
            checked={selected}
            onChange={() => rental.id && onToggleSelect?.(rental.id)}
            disabled={isDeleting}
            aria-label={name}
          />
        )}
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <RentalIcon />
        <Box style={{ minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <MediaBadge rental={rental} />
          </Group>
          <VendorCell rental={rental} />
        </Box>
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 260 }}>
      <RouteCell rental={rental} />
    </Table.Td>
    <Table.Td style={{ width: 120 }}>
      <CostCell rental={rental} />
    </Table.Td>
    <Table.Td style={{ width: 120 }}>
      <StatusBadge rental={rental} />
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <RowActions
        rental={rental}
        onEdit={onEdit}
        onDelete={onDelete}
        onMedia={onMedia}
        onSetStatus={onSetStatus}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
      />
    </Table.Td>
  </Table.Tr>
);

const RentalTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 260 }}>
      <Skeleton height={30} width={180} />
    </Table.Td>
    <Table.Td style={{ width: 120 }}>
      <Skeleton height={14} width={70} />
    </Table.Td>
    <Table.Td style={{ width: 120 }}>
      <Skeleton height={18} width={70} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const RentalCardComponent: React.FC<CardProps> = ({
  rental,
  name,
  onEdit,
  onDelete,
  onMedia,
  onSetStatus,
  isUpdating,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["rentals", "common"]);

  const toggleSelect = () => rental.id && onToggleSelect?.(rental.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];
    const canEdit = recordHasRight(rental, "edit");
    const statusAction = onSetStatus && canEdit ? nextStatusAction(rental, t) : null;

    if (onMedia) {
      actions.push({
        key: "media",
        icon: "Photo",
        label: t("rentals:list.media", { defaultValue: "Condition media" }),
        color: "grape",
        onClick: () => onMedia(rental),
        disabled: isDeleting,
        secondary: true,
      });
    }

    if (statusAction && onSetStatus) {
      actions.push({
        key: `status:${statusAction.next}`,
        icon: statusAction.icon,
        label: statusAction.label,
        color: statusAction.color,
        onClick: () => onSetStatus(rental, statusAction.next),
        loading: isUpdating(rental.id, `status:${statusAction.next}`),
        disabled: isDeleting || (isUpdating(rental.id) && !isUpdating(rental.id, `status:${statusAction.next}`)),
        secondary: true,
      });
    }

    if (onEdit && canEdit) {
      actions.push({ key: "edit", icon: "Edit", label: t("common:buttons.edit"), color: "blue", onClick: () => onEdit(rental), disabled: isDeleting });
    }

    if (onDelete && recordHasRight(rental, "remove")) {
      actions.push({ key: "delete", icon: "Trash", label: t("common:buttons.delete"), color: "red", onClick: () => onDelete(rental), loading: isDeleting, disabled: isDeleting });
    }

    return actions;
  }, [onMedia, onEdit, onDelete, onSetStatus, isUpdating, rental, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && (
          <Checkbox mt={2} checked={selected} onChange={toggleSelect} onClick={(e) => e.stopPropagation()} disabled={isDeleting} aria-label={name} />
        )}
        <RentalIcon size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap" justify="space-between">
            <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
              <Text size="md" fw={600} lineClamp={1}>
                {name}
              </Text>
              <MediaBadge rental={rental} />
            </Group>
            <StatusBadge rental={rental} />
          </Group>
          <Box mt={2}>
            <VendorCell rental={rental} />
          </Box>
          <Box mt={6}>
            <RouteCell rental={rental} />
          </Box>
          <Box mt={6}>
            <CostCell rental={rental} />
          </Box>
        </Box>
        {showActions && (
          <RowActions
            rental={rental}
            onEdit={onEdit}
            onDelete={onDelete}
            onMedia={onMedia}
            onSetStatus={onSetStatus}
            isUpdating={isUpdating}
            isDeleting={isDeleting}
          />
        )}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box hiddenFrom="sm" onClick={selectable ? toggleSelect : undefined} style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}>
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const RentalCard = React.memo(RentalCardComponent);

const RentalCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={7} sizes={["md", "md", "md", "xs", "md", "xs", "md"]} gap={3} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

const RentalsListComponent: React.FC<RentalsListProps> = ({
  data = [],
  loading = false,
  isLoaded,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  onEditRental,
  onDeleteRental,
  onManageMedia,
  onSetStatus,
  isUpdating = () => false,
  isRemoving = () => false,
  highlightedId,
  onAddRental,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t } = useTranslation(["rentals", "common"]);
  const { isDesktop } = useAppEnvironment();
  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onSetStatus, onManageMedia, onEditRental, onDeleteRental);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleEdit = useCallback(
    (rental: EventRental) => {
      closeAllCards();
      onEditRental?.(rental);
    },
    [onEditRental, closeAllCards]
  );

  const handleDelete = useCallback(
    (rental: EventRental) => {
      closeAllCards();
      onDeleteRental?.(rental);
    },
    [onDeleteRental, closeAllCards]
  );

  const handleMedia = useCallback(
    (rental: EventRental) => {
      closeAllCards();
      onManageMedia?.(rental);
    },
    [onManageMedia, closeAllCards]
  );

  // ⚠ The FIRST read only — see `isLoaded` on the props. ▶ npm run check-list-states
  const showSkeleton = !isLoaded;

  if (!showSkeleton && data.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("rentals:emptyState.noMatch", { defaultValue: "No rentals match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("rentals:emptyState.title", { defaultValue: "No rentals yet" })}
          description={t("rentals:emptyState.description", {
            defaultValue: "Add a rented asset — its pickup/drop-off, cost and condition photos — for this event.",
          })}
          iconName="Package"
          actionLabel={t("rentals:emptyState.actionLabel", { defaultValue: "Add rental" })}
          onAction={onAddRental}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (rental: EventRental): boolean =>
    isSelectable && ((!!onEditRental && recordHasRight(rental, "edit")) || (!!onDeleteRental && recordHasRight(rental, "remove")));

  const rowProps = (rental: EventRental): RowProps => ({
    rental,
    name: rental.name || "—",
    onEdit: onEditRental ? handleEdit : undefined,
    onDelete: onDeleteRental ? handleDelete : undefined,
    onMedia: onManageMedia ? handleMedia : undefined,
    onSetStatus,
    isUpdating,
    highlighted: rental.id === highlightedId,
    isDeleting: isRemoving(rental.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(rental),
    selected: selection?.isSelected(rental.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = data
    .filter((r) => rowIsSelectable(r))
    .map((r) => r.id)
    .filter((id): id is string => !!id);

  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("rentals:list.columns.name", { defaultValue: "Rental" })}</Table.Th>
                <Table.Th style={{ width: 260 }}>{t("rentals:list.columns.route", { defaultValue: "Pickup → Drop-off" })}</Table.Th>
                <Table.Th style={{ width: 120 }}>{t("rentals:list.columns.cost", { defaultValue: "Total" })}</Table.Th>
                <Table.Th style={{ width: 120 }}>{t("rentals:list.columns.status", { defaultValue: "Status" })}</Table.Th>
                <Table.Th style={{ width: 160 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => <RentalTableRowSkeleton key={i} selectionColumn={isSelectable} />)
                : data.map((rental) => <RentalTableRow key={rental.id} {...rowProps(rental)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <RentalCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {data.map((rental) => (
          <RentalCard
            key={rental.id}
            {...rowProps(rental)}
            forceClose={shouldForceClose(rental.id || "")}
            onForceCloseComplete={() => handleForceCloseComplete(rental.id || "")}
            onSwipeStateChange={(isOpen) => isOpen && openCard(rental.id || "")}
          />
        ))}
      </Stack>
      {loadMore}
    </Stack>
  );
};

export const RentalsList = React.memo(RentalsListComponent);
